import type { KeyboardEvent } from 'react';

/**
 * Roving tabindex for composite widgets (WAI-ARIA tabs and radio groups): the group is a single
 * tab stop and the arrow keys move between its items.
 */
export interface RovingOptions {
  /** Selector of the items inside the group element. */
  selector: string;
  /** Moving focus also selects the item (radio groups, tabs with automatic activation). */
  activate?: boolean;
}

function isEnabled(el: HTMLElement): boolean {
  return el.getAttribute('aria-disabled') !== 'true' && !(el as HTMLButtonElement).disabled;
}

function isCurrent(el: HTMLElement): boolean {
  return el.getAttribute('aria-checked') === 'true' || el.getAttribute('aria-selected') === 'true';
}

function itemsOf(group: HTMLElement, selector: string): HTMLElement[] {
  return [...group.querySelectorAll<HTMLElement>(selector)].filter(
    (el) => el.getClientRects().length > 0,
  );
}

/**
 * Keyboard handler for the group element: Left/Right and Up/Down move to the previous/next
 * item (Left/Right follow the reading direction), Home/End to the first/last one. Disabled
 * items are skipped and focus wraps around.
 */
export function rovingKeyDown(event: KeyboardEvent<HTMLElement>, options: RovingOptions): void {
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  const group = event.currentTarget;
  const items = itemsOf(group, options.selector);
  const from = items.indexOf(event.target as HTMLElement);
  if (from < 0) return;
  const rtl = getComputedStyle(group).direction === 'rtl';
  let index: number;
  let step: number;
  switch (event.key) {
    case 'ArrowRight':
      [index, step] = [from, rtl ? -1 : 1];
      break;
    case 'ArrowLeft':
      [index, step] = [from, rtl ? 1 : -1];
      break;
    case 'ArrowDown':
      [index, step] = [from, 1];
      break;
    case 'ArrowUp':
      [index, step] = [from, -1];
      break;
    case 'Home':
      [index, step] = [-1, 1];
      break;
    case 'End':
      [index, step] = [items.length, -1];
      break;
    default:
      return;
  }
  event.preventDefault();
  const wrap = event.key !== 'Home' && event.key !== 'End';
  for (let n = 0; n < items.length; n++) {
    index += step;
    if (wrap) index = (index + items.length) % items.length;
    const item = items[index];
    if (!item) return;
    if (!isEnabled(item)) continue;
    item.focus();
    if (options.activate && !isCurrent(item)) item.click();
    return;
  }
}
